import { MaybeAsync } from '@angular/router';
import { inject } from '@angular/core';
import {
  combineLatest,
  from,
  isObservable,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import { openConfirmDialog } from 'mt-ui-lib/components/dialog';

/**
 * Функция для проверки возможности выхода из роута
 * с подтверждением в модалке.
 * @param maybeAsyncHasChanges флаги наличия изменений
 * @param componentOptions параметры компонента
 * @param modalOptions параметры модалки
 * @example
 *  canDeactivate: [
      () => {
        const store: Store<IAppState> = inject(Store);
        return canDeactivateWithModal([
          store.select(selectDeviceHasChanges),
        ]);
      },
    ],
 * @returns
 */
export const canDeactivateWithModal = (
  maybeAsyncHasChanges: [MaybeAsync<boolean>, ...MaybeAsync<boolean>[]],
): Observable<boolean> => {
  const dialog = inject(Dialog);
  const asAsyncHasChanges: Observable<boolean>[] = maybeAsyncHasChanges.map(
    (h) => {
      if (isObservable(h)) {
        return h;
      } else if (h instanceof Promise) {
        return from(h);
      } else {
        return of(h);
      }
    },
  );
  return combineLatest(asAsyncHasChanges).pipe(
    take(1),
    switchMap(
      (hasChanges) =>
        new Observable<boolean>((observer) => {
          if (hasChanges.every((h) => !h)) {
            observer.next(true);
            observer.complete();

            return;
          }

          const ref = openConfirmDialog(dialog);

          from(ref.closed)
            .pipe(
              tap((res) => {
                observer.next(res);
                observer.complete();
              }),
            )
            .subscribe();
        }),
    ),
  );
};
